import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/css';
import { FileQuestion, Clock } from 'lucide-react';
import Navbar from '../../shared/components/navbar/navbar';
import Footer from '../../shared/components/footer/footer';
import { Button } from '../../shared/ui/button/button';

const pageWrapper = css({
  minHeight: '100vh',
  background: 'var(--g-100)',
  display: 'flex',
  flexDirection: 'column',
});

const wrapper = css({
  width: '100%',
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.2)',
  padding: 'var(--spacing-lg)',
});

const container = css({
  width: '100%',
  maxWidth: '600px',
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-md)',
  boxShadow: 'var(--shadow-300)',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  overflow: 'hidden',
  transition: 'all 0.2s ease-in-out',
});

const content = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 'var(--spacing-3xl) var(--spacing-lg)',
  textAlign: 'center',
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-2xl) var(--spacing-md)',
  },
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-xl) var(--spacing-sm)',
  },
});

const iconContainer = css({
  width: '120px',
  height: '120px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.6)',
  borderRadius: '50%',
  marginBottom: 'var(--spacing-lg)',
  '@media (max-width: 768px)': {
    width: '100px',
    height: '100px',
  },
  '@media (max-width: 480px)': {
    width: '80px',
    height: '80px',
  },
});

const icon = css({
  color: 'var(--pc-600)',
});

const title = css({
  fontSize: 'clamp(5rem, 10vw, 8rem)',
  fontWeight: 700,
  color: 'var(--pc-700)',
  lineHeight: 1,
  marginBottom: 'var(--spacing-md)',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '4px',
    backgroundColor: 'var(--pc-400)',
    borderRadius: '2px',
  },
});

const subtitle = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 600,
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-md)',
  marginTop: 'var(--spacing-md)',
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-500)',
  },
});

const description = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-700)',
  lineHeight: 1.6,
  marginBottom: 'var(--spacing-md)',
  maxWidth: '80%',
  '@media (max-width: 768px)': {
    maxWidth: '90%',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-350)',
    maxWidth: '100%',
  },
});

const countdown = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  marginBottom: 'var(--spacing-xl)',
  padding: 'var(--spacing-sm) var(--spacing-lg)',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.5)',
  borderRadius: 'var(--spacing-sm)',
  border: '1px solid rgba(var(--pcrgba-200), 0.6)',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-xs) var(--spacing-md)',
  },
});

const countdownIcon = css({
  color: 'var(--pc-500)',
});

const countdownText = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-800)',
  margin: 0,
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-300)',
  },
});

const countdownNumber = css({
  fontWeight: 700,
  color: 'var(--pc-700)',
  fontSize: 'var(--fs-400)',
});

const buttons = css({
  display: 'flex',
  gap: 'var(--spacing-md)',
  flexWrap: 'wrap',
  justifyContent: 'center',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: 'var(--spacing-sm)',
    width: '100%',
  },
});

const buttonPrimary = css({
  minWidth: '200px',
  '@media (max-width: 768px)': {
    width: '100%',
    minWidth: 'unset',
  },
});

const buttonSecondary = css({
  minWidth: '200px',
  backgroundColor: 'var(--g-200)',
  color: 'var(--g-800)',
  '&:hover': {
    backgroundColor: 'var(--g-300)',
  },
  '@media (max-width: 768px)': {
    width: '100%',
    minWidth: 'unset',
  },
});

export function NotFound() {
  const navigate = useNavigate();
  const [countdownValue, setCountdownValue] = useState(10);

  // Set up countdown timer
  useEffect(() => {
    if (countdownValue <= 0) {
      // Redirect to home page
      navigate('/', { replace: true });
      return;
    }

    // Create timer
    const timer = setTimeout(() => {
      setCountdownValue((prevCount) => prevCount - 1);
    }, 1000);

    // Clean up timer
    return () => clearTimeout(timer);
  }, [countdownValue, navigate]);

  return (
    <>
      <Navbar />
      <div className={pageWrapper}>
        <div className={wrapper}>
          <div className={container}>
            <div className={content}>
              <div className={iconContainer}>
                <FileQuestion size={80} className={icon} />
              </div>

              <h1 className={title}>404</h1>

              <h2 className={subtitle}>Página não encontrada</h2>

              <p className={description}>
                A página que você está procurando não existe ou foi movida. Verifique a URL ou
                retorne à página inicial.
              </p>

              <div className={countdown}>
                <Clock size={18} className={countdownIcon} />
                <p className={countdownText}>
                  Redirecionando para a página inicial em{' '}
                  <span className={countdownNumber}>{countdownValue}</span> segundos...
                </p>
              </div>

              <div className={buttons}>
                <Button className={buttonPrimary} onClick={() => navigate('/')}>
                  Voltar para a página inicial
                </Button>

                <Button className={buttonSecondary} onClick={() => navigate(-1)}>
                  Voltar para a página anterior
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default NotFound;
